import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, DocumentArrowUpIcon, XMarkIcon, CheckCircleIcon, DocumentTextIcon, CheckIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';

import { QUESTION_TYPES } from '../../constants/questionTypes';

const CREATION_MODE = {
  AI: 'ai',
  MANUAL: 'manual',
};

/** Sent to /api as constraints.questionTypes; MC UI maps to both single- and multi-select types. */
const QUESTION_TYPE_MC_SINGLE = 'Multiple Choice (Single)';
const QUESTION_TYPE_MC_MULTIPLE = 'Multiple Choice (Multiple)';

const AssessmentCreate = ({
  isModal = false,
  creationMode = CREATION_MODE.AI,
  jobPostingId: propJobPostingId,
  screeningName = '',
  onCreated,
  registerSubmit,
  onGeneratedSections,
  onLoadingChange,
  onGenerationStart,
  onValidationChange,
  initialDraft = null,
  onDraftChange,
  isPassageReadingMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const linkedJobPostingId = propJobPostingId || location.state?.jobPostingId || '';
  const prefill = location.state?.prefill;
  const returnTo = location.state?.returnTo || '/dashboard/assessments';
  const fromHireLoop = location.state?.fromHireLoop || false;
  const backgroundLocation = location.state?.backgroundLocation;
  const modal = location.state?.modal;
  const { user } = useAuth();
  const MAX_AI_QUESTIONS = user?.limits?.max_questions_per_assessment || 40;
  const MAX_DURATION_MINUTES = MAX_AI_QUESTIONS * 2;
  const isManualMode = creationMode === CREATION_MODE.MANUAL;
  const defaultAdditionalContext = isPassageReadingMode
    ? 'Generate a passage reading assessment to evaluate communication skills including fluency, pronunciation, clarity, and confidence.'
    : '';
  const draftFormData = initialDraft?.formData || {};
  const draftNumberOfQuestions = parseInt(draftFormData.numberOfQuestions, 10);

  // Duration from question count: 1 minute per question, cap at MAX, minimum 5
  const durationFromQuestionCount = (n) => {
    const count = n || 0;
    return Math.min(MAX_DURATION_MINUTES, Math.max(5, count));
  };

  const [formData, setFormData] = useState(() => ({
    title: draftFormData.title || '',
    category: draftFormData.category || '',
    difficulty: draftFormData.difficulty || '',
    additionalContext:
      draftFormData.additionalContext != null
        ? draftFormData.additionalContext
        : defaultAdditionalContext,
    jobPosting: linkedJobPostingId || draftFormData.jobPosting || '',
    numberOfQuestions:
      !Number.isNaN(draftNumberOfQuestions) && draftNumberOfQuestions > 0
        ? Math.min(MAX_AI_QUESTIONS, draftNumberOfQuestions)
        : (isPassageReadingMode ? 1 : 10),
  }));
  const assessmentTypeOptions = ['Technical', 'Behavioral', 'Cultural'];
  if (isPassageReadingMode) {
    assessmentTypeOptions.push('Communication');
  }
  const assessmentCategoryOptions = [
    { value: 'sales', label: 'Sales', description: 'For SDRs, AEs, and revenue roles.' },
    { value: 'marketing', label: 'Marketing', description: 'For growth, content, and brand roles.' },
    { value: 'software-engineering', label: 'Software Engineering', description: 'For backend, frontend, and full-stack roles.' },
    { value: 'data', label: 'Data & Analytics', description: 'For data analysts, scientists, and BI roles.' },
    { value: 'product', label: 'Product Management', description: 'For PMs and product strategy roles.' },
    { value: 'recruiting', label: 'Talent & Recruiting', description: 'For recruiters and talent partners.' },
    { value: 'customer-success', label: 'Customer Success', description: 'For CS, onboarding, and account roles.' },
    { value: 'operations', label: 'Operations', description: 'For business, ops, and enablement roles.' },
    { value: 'finance', label: 'Finance', description: 'For FP&A, accounting, and finance roles.' },
    { value: 'people', label: 'People & HR', description: 'For HRBPs and people operations roles.' },
    { value: 'it', label: 'IT', description: 'For IT support, systems, and infrastructure roles.' },
    { value: 'engineering', label: 'Engineering', description: 'For engineering roles across disciplines.' },
    { value: 'general', label: 'General', description: 'A broad evaluation for mixed roles.' },
    { value: 'communication', label: 'Communication', description: 'For roles requiring strong verbal and written communication skills.' },
    { value: 'customer-support', label: 'Customer Support', description: 'For technical support, customer service, and helpdesk.' },
    { value: 'design-ux', label: 'Design & UX', description: 'For UX/UI designers, graphic designers, and researchers.' },
    { value: 'accounting', label: 'Accounting', description: 'For accountants, bookkeepers, and tax professionals.' },
    { value: 'legal', label: 'Legal', description: 'For legal counsel, paralegals, and compliance officers.' },
    { value: 'healthcare', label: 'Healthcare', description: 'For nurses, medical assistants, and healthcare administrators.' },
    { value: 'education', label: 'Education & Training', description: 'For teachers, trainers, and instructional designers.' },
    { value: 'administration', label: 'Administration', description: 'For administrative assistants, office managers, and receptionists.' },
    { value: 'project-management', label: 'Project Management', description: 'For Scrum masters, Agile coaches, and project coordinators.' },
    { value: 'business-development', label: 'Business Development', description: 'For partnerships, alliances, and B2B growth roles.' },
    { value: 'supply-chain', label: 'Supply Chain & Logistics', description: 'For procurement, inventory, and logistics managers.' },
    { value: 'quality-assurance', label: 'Quality Assurance', description: 'For manual/automation testers and QA engineers.' },
    { value: 'cybersecurity', label: 'Cybersecurity', description: 'For security analysts, penetration testers, and compliance.' },
  ];
  const assessmentDifficultyOptions = [
    {
      value: 'beginner',
      label: 'Beginner',
      description: 'Great for interns or entry‑level candidates.',
    },
    {
      value: 'intermediate',
      label: 'Intermediate',
      description: 'Ideal for solid individual contributors.',
    },
    {
      value: 'advanced',
      label: 'Advanced',
      description: 'Suited for seniors and specialists.',
    },
    {
      value: 'expert',
      label: 'Expert',
      description: 'For principals, leads, and subject‑matter experts.',
    },
  ];
  const questionTypeUiOptions = [
    {
      key: 'multiple-choice',
      label: 'Multiple Choice',
      apiValues: [QUESTION_TYPE_MC_SINGLE, QUESTION_TYPE_MC_MULTIPLE],
    },
    { key: 'true-false', label: 'True/False', apiValues: ['True/False'] },
    { key: 'short-answer', label: 'Short Answer', apiValues: ['Short Answer'] },
    { key: QUESTION_TYPES.FILL_IN_BLANKS, label: 'Fill in the Blank', apiValues: ['Fill in the Blank'] },
    ...(isPassageReadingMode ? [{ key: 'passage-reading', label: 'Pronunciation', apiValues: ['Passage Reading'] }] : []),
  ];
  // Kept as empty array so any stale reference (e.g. hot reload) does not throw
  const disabledQuestionTypes = [];

  const [selectedAssessmentTypes, setSelectedAssessmentTypes] = useState(() => {
    if (Array.isArray(initialDraft?.selectedAssessmentTypes)) {
      return initialDraft.selectedAssessmentTypes;
    }
    return isPassageReadingMode ? ['Communication'] : [];
  });
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState(() => {
    if (Array.isArray(initialDraft?.selectedQuestionTypes)) {
      return initialDraft.selectedQuestionTypes;
    }
    return isPassageReadingMode ? ['Passage Reading'] : [];
  });
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isDifficultyOpen, setIsDifficultyOpen] = useState(false);

  // JD File upload state (for AI mode without existing job posting)
  const [jdFile, setJdFile] = useState(initialDraft?.jdFile || null);
  const [jdProcessing, setJdProcessing] = useState(false);
  const [extractedJobData, setExtractedJobData] = useState(initialDraft?.extractedJobData || null);
  const [isDraggingJd, setIsDraggingJd] = useState(false);
  const [showJdUploadModal, setShowJdUploadModal] = useState(false);
  const jdFileInputRef = useRef(null);

  const MAX_JD_FILE_SIZE_MB = 10;
  const MAX_JD_FILE_SIZE_BYTES = MAX_JD_FILE_SIZE_MB * 1024 * 1024;

  const validateJdFile = (file) => {
    const lowerName = (file.name || '').toLowerCase();
    const isPdf = lowerName.endsWith('.pdf');
    const isDocx = lowerName.endsWith('.docx');
    const isDoc = lowerName.endsWith('.doc');
    if (!isPdf && !isDocx && !isDoc) {
      toast.error('Please upload a PDF, DOC, or DOCX file');
      return false;
    }
    if (file.size > MAX_JD_FILE_SIZE_BYTES) {
      toast.error(`File size must be under ${MAX_JD_FILE_SIZE_MB}MB`);
      return false;
    }
    return true;
  };

  const handleJdFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      event.target.value = '';
      return;
    }
    if (!validateJdFile(file)) {
      event.target.value = '';
      return;
    }
    setJdFile(file);
    event.target.value = '';
    processJdFile(file);
  };

  const handleJdFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingJd(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!validateJdFile(file)) return;
    setJdFile(file);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
    processJdFile(file);
  };

  const handleJdDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingJd(true);
  };

  const handleJdDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingJd(false);
  };

  const handleJdRemoveFile = () => {
    setJdFile(null);
    setExtractedJobData(null);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  const processJdFile = async (file) => {
    try {
      setJdProcessing(true);
      const formDataUpload = new FormData();
      formDataUpload.append('jdFile', file);

      const response = await axios.post('/api/job-posts/generate-from-jd', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.jobPosting) {
        const generated = response.data.jobPosting;
        setExtractedJobData({
          title: generated.title || '',
          description: generated.description || '',
          keyResponsibilities: generated.keyResponsibilities || '',
          experience: generated.experience || '',
          skills: Array.isArray(generated.skills) ? generated.skills : [],
          keyAttributes: generated.keyAttributes || '',
          location: generated.location || '',
          candidateQualification: generated.candidateQualification || '',
        });
        // Auto-fill the title if empty
        if (!formData.title && generated.title) {
          setFormData((prev) => ({
            ...prev,
            title: generated.title,
          }));
        }
        toast.success('JD processed successfully! Job details extracted.');
      } else {
        toast.error(response.data.message || 'Failed to process JD file');
        setJdFile(null);
      }
    } catch (error) {
      console.error('Error processing JD file:', error);
      toast.error(error.response?.data?.message || 'Failed to process JD file');
      setJdFile(null);
    } finally {
      setJdProcessing(false);
    }
  };

  // In screening modal flow, keep title empty so user must enter it.

  // Apply prefill if provided via navigation state (clamp to limits)
  useEffect(() => {
    if (prefill && !initialDraft?.formData) {
      setFormData((prev) => {
        const next = { ...prev, ...prefill, jobPosting: linkedJobPostingId || prev.jobPosting };
        const questionsNum = parseInt(next.numberOfQuestions, 10);
        if (!Number.isNaN(questionsNum) && questionsNum > MAX_AI_QUESTIONS) {
          next.numberOfQuestions = MAX_AI_QUESTIONS;
        }
        return next;
      });
    }
  }, [prefill, linkedJobPostingId, initialDraft?.formData, MAX_AI_QUESTIONS]);

  // Persist draft state to parent modal so step navigation does not lose user input.
  useEffect(() => {
    if (!isModal || typeof onDraftChange !== 'function') return undefined;
    const frameId = requestAnimationFrame(() => {
      onDraftChange({
        formData,
        selectedAssessmentTypes,
        selectedQuestionTypes,
        jdFile,
        extractedJobData,
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [
    isModal,
    onDraftChange,
    formData,
    selectedAssessmentTypes,
    selectedQuestionTypes,
    jdFile,
    extractedJobData,
  ]);
  const [loading, setLoading] = useState(false);
  const shouldShowJdModalCta = !isManualMode && !linkedJobPostingId;

  // AI Recommendations loading state
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  // Notify parent of loading state changes (use requestAnimationFrame to avoid render-time updates)
  useEffect(() => {
    if (typeof onLoadingChange === 'function') {
      const frameId = requestAnimationFrame(() => {
        onLoadingChange(loadingRecommendations || loading);
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [loadingRecommendations, loading, onLoadingChange]);

  // Check if form is valid - different requirements for manual mode
  // For AI mode: need title, category, assessment types, question types, AND additional context.
  const isFormValid = isManualMode
    ? Boolean(formData.title?.trim() && formData.category)
    : Boolean(
      formData.title?.trim() &&
      formData.category &&
      selectedAssessmentTypes.length > 0 &&
      selectedQuestionTypes.length > 0 &&
      formData.additionalContext?.trim()
    );

  // Notify parent of validation state changes (use setTimeout to avoid render-time updates)
  useEffect(() => {
    if (typeof onValidationChange === 'function') {
      // Use requestAnimationFrame to ensure this runs after render
      const frameId = requestAnimationFrame(() => {
        onValidationChange(isFormValid);
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [isFormValid, onValidationChange]);

  // Also notify on mount to set initial validation state
  useEffect(() => {
    if (typeof onValidationChange === 'function' && isModal) {
      const frameId = requestAnimationFrame(() => {
        onValidationChange(isFormValid);
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'numberOfQuestions') {
      const num = parseInt(value, 10);
      if (value !== '' && !Number.isNaN(num)) {
        nextValue = Math.min(isPassageReadingMode ? 3 : MAX_AI_QUESTIONS, Math.max(1, num));
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const submit = useCallback(async () => {
    // Early validation check - different for manual mode
    // For AI mode: additional context is mandatory; JD upload is optional
    const isValid = isManualMode
      ? Boolean(formData.title?.trim())
      : Boolean(
        formData.title?.trim() &&
        selectedAssessmentTypes.length > 0 &&
        selectedQuestionTypes.length > 0 &&
        formData.additionalContext?.trim()
      );

    if (!isValid) {
      if (isModal) {
        return;
      }
      if (!formData.title || formData.title.trim() === '') {
        toast.error('Please enter an assessment title');
        return;
      }
      if (!isManualMode) {
        if (selectedAssessmentTypes.length === 0) {
          toast.error('Please select at least one assessment type');
          return;
        }
        if (selectedQuestionTypes.length === 0) {
          toast.error('Please select at least one question type');
          return;
        }
        if (!formData.additionalContext || formData.additionalContext.trim() === '') {
          toast.error('Please add context so we can generate relevant questions');
          return;
        }
      }
      return;
    }

    // Manual mode: just pass form data and move to next step (no AI generation)
    if (isManualMode) {
      const manualFormData = {
        title: formData.title.trim(),
        duration: 30,
        passingScore: 70,
        role: isPassageReadingMode ? 'Communication' : 'IT',
        additionalContext: formData.additionalContext || '',
        assessmentTypes: isPassageReadingMode ? ['Communication'] : [],
        questionTypes: isPassageReadingMode ? ['Passage Reading'] : [],
        jobPosting: linkedJobPostingId || null,
        category: formData.category || '',
        difficulty: formData.difficulty || '',
      };

      logger.log('📋 Manual mode: passing form data to parent:', manualFormData);

      if (typeof onCreated === 'function') {
        onCreated({
          formData: manualFormData,
          aiSections: null,
        });
      }
      return;
    }

    try {
      const numQuestions = Math.min(
        isPassageReadingMode ? 3 : MAX_AI_QUESTIONS,
        Math.max(1, parseInt(formData.numberOfQuestions, 10) || (isPassageReadingMode ? 1 : 10))
      );
      if (typeof onGenerationStart === 'function') {
        onGenerationStart(numQuestions);
      }

      // Don't create assessment yet - just generate questions
      // Assessment will be created only after all steps are completed
      setLoadingRecommendations(true);

      logger.log('Generating AI assessment questions...');
      const duration = durationFromQuestionCount(numQuestions);

      // Build request data - use jobPostingId if available, otherwise use extractedJobData from JD upload
      const requestData = {
        constraints: {
          title: formData.title.trim(),
          category: formData.category || undefined,
          difficulty: formData.difficulty || undefined,
          assessmentType: selectedAssessmentTypes.join(', '),
          questionTypes: selectedQuestionTypes,
          categories: selectedAssessmentTypes,
          additionalContext: formData.additionalContext || '',
          duration,
          numberOfQuestions: numQuestions,
        },
      };

      // Use existing job posting ID if available, otherwise use extracted JD data
      if (linkedJobPostingId) {
        requestData.jobPostingId = linkedJobPostingId;
        logger.log('🔗 Using linked job posting ID:', linkedJobPostingId);
      } else if (extractedJobData) {
        requestData.jobPostingData = extractedJobData;
        logger.log('📄 Using extracted JD data:', extractedJobData);
      } else {
        // JD optional fallback: provide minimal context so backend can generate from prompt-only flow.
        requestData.jobPostingData = {
          title: formData.title.trim(),
          description: formData.additionalContext || '',
          skills: [],
        };
        logger.log('📝 Using prompt-only fallback job data for AI generation');
      }

      logger.log('🚀 Request data for AI recommendations:', requestData);
      const recommendationsResponse = await axios.post(
        '/api/assessments/recommendations',
        requestData
      );

      if (recommendationsResponse.data.success && recommendationsResponse.data.recommendations) {
        const recommendations = recommendationsResponse.data.recommendations;
        logger.log('✅ AI recommendations generated:', recommendations);

        // Pass sections AND form data to parent for use in AssessmentBuild and final creation
        const sections = recommendations.sections || null;
        logger.log('📋 Generated sections:', sections);

        if (typeof onGeneratedSections === 'function') {
          onGeneratedSections(sections);
        }

        // Pass form data to parent so it can be used when creating assessment on final step
        if (typeof onCreated === 'function') {
          onCreated({
            formData: {
              title: formData.title.trim(),
              duration, // Computed from numberOfQuestions (1 minute per question)
              passingScore: 70, // Default, can be updated
              role: isPassageReadingMode ? 'Communication' : 'IT', // Default, can be updated
              additionalContext: formData.additionalContext || '',
              assessmentTypes: selectedAssessmentTypes,
              questionTypes: selectedQuestionTypes,
              jobPosting: linkedJobPostingId || null,
              category: formData.category || '',
              difficulty: formData.difficulty || '',
            },
            aiSections: sections, // Ensure we always pass sections (even if null)
          });
        }

        // In modal flow, we don't navigate - modal handles step transitions
        if (isModal) {
          return;
        }

        // For non-modal flow, we'd need to handle navigation differently
        // But for now, modal is the primary use case
      } else {
        throw new Error('Failed to generate assessment questions');
      }
    } catch (error) {
      console.error('Error generating assessment questions:', error);
      console.error('Error response:', error.response?.data);

      // Handle specific error codes from backend
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message;

      if (
        error.response?.status === 429 ||
        errorCode === 'ai_cooldown' ||
        errorCode === 'ai_rate_limit'
      ) {
        toast.error('AI service is temporarily unavailable. Please try again in a few minutes.');
      } else if (error.response?.status === 503 || errorCode === 'ai_not_configured') {
        toast.error('AI service is not configured. Please contact support.');
      } else if (errorCode === 'ai_model_error') {
        toast.error('AI model configuration error. Please contact support.');
      } else if (error.response?.data?.errors) {
        const validationError = error.response.data.errors[0]?.msg || 'Validation error';
        toast.error(validationError);
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error(
          'Failed to generate assessment questions. Please check the console for details and try again.'
        );
      }
    } finally {
      setLoadingRecommendations(false);
    }
  }, [
    formData,
    selectedAssessmentTypes,
    selectedQuestionTypes,
    linkedJobPostingId,
    extractedJobData,
    isModal,
    isManualMode,
    onCreated,
    onGeneratedSections,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submit();
  };

  // Register submit function using requestAnimationFrame to avoid render-time updates
  useEffect(() => {
    if (isModal && typeof registerSubmit === 'function') {
      // Use requestAnimationFrame to ensure this runs after render
      const frameId = requestAnimationFrame(() => {
        registerSubmit(submit);
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [isModal, registerSubmit, submit]);

  return (
    <div className={isModal ? '' : 'min-h-screen bg-gray-50 py-8'}>
      <div className={isModal ? 'max-w-none px-0' : 'max-w-3xl mx-auto px-4 sm:px-6 lg:px-8'}>
        {!isModal && (
          <>
            {/* Header */}
            <div className="mb-8">
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900">Create Assessment</h1>
                <p className="mt-2 text-gray-600">
                  Step 1: Fill in the basic information for your assessment
                </p>
                {linkedJobPostingId && (
                  <p className="mt-2 inline-block px-3 py-1 text-xs rounded-full bg-[#007BFF]/5 text-[#007BFF] border border-[#007BFF]/30">
                    : {linkedJobPostingId}
                  </p>
                )}
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <div className="bg-[#007BFF] text-white rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    1
                  </div>
                  <div className="ml-2 text-sm font-medium text-[#007BFF]">Metadata</div>
                </div>
                <div className="mx-4 h-px w-16 bg-gray-300"></div>
                <div className="flex items-center">
                  <div className="bg-gray-300 text-gray-500 rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <div className="ml-2 text-sm font-medium text-gray-500">Build Content</div>
                </div>
                <div className="mx-4 h-px w-16 bg-gray-300"></div>
                <div className="flex items-center">
                  <div className="ml-2 text-sm font-medium text-gray-500">Review & Publish</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Playwright Debug Info */}
        <div id="pw-debug-info" style={{ display: 'none' }} data-debug={JSON.stringify({
          title: formData.title,
          category: formData.category,
          context: formData.additionalContext,
          assessmentTypes: selectedAssessmentTypes,
          questionTypes: selectedQuestionTypes,
          isFormValid,
          isManualMode
        })}></div>

        {/* Form */}
        <div className={isModal ? '' : 'bg-white rounded-lg shadow p-6'}>
          {showJdUploadModal && shouldShowJdModalCta && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
              <button
                type="button"
                data-testid="close-upload-modal-overlay"
                aria-label="Close upload modal"
                onClick={() => {
                  if (!jdProcessing) setShowJdUploadModal(false);
                }}
                className="absolute inset-0 bg-black/20"
              />
              <div className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Upload Job Description</h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {isPassageReadingMode
                        ? "Required to generate a relevant communication passage."
                        : 'Required to generate relevant assessment questions.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="close-upload-modal-x-btn"
                    onClick={() => {
                      if (!jdProcessing) setShowJdUploadModal(false);
                    }}
                    disabled={jdProcessing}
                    className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>

                <div
                  onDrop={handleJdFileDrop}
                  onDragOver={handleJdDragOver}
                  onDragLeave={handleJdDragLeave}
                  onClick={() => !jdProcessing && jdFileInputRef.current?.click()}
                  className={`rounded-xl border-2 border-dashed p-5 transition-all duration-200 ${
                    isDraggingJd
                      ? 'border-[#007BFF] bg-[#007BFF]/5'
                      : extractedJobData
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <input
                    id="jd-file-input"
                    data-testid="jd-file-input"
                    ref={jdFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleJdFileChange}
                    className="hidden"
                  />
                  {jdProcessing ? (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                      <p className="text-sm font-medium text-[#007BFF]">Processing JD...</p>
                      <p className="text-xs text-gray-500">Extracting job details from your file</p>
                    </div>
                  ) : extractedJobData ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircleIcon className="h-4 w-4" />
                        <p className="text-sm font-semibold">JD processed successfully</p>
                      </div>
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Job title:</span> {extractedJobData.title || '—'}
                      </p>
                      {Array.isArray(extractedJobData.skills) && extractedJobData.skills.length > 0 && (
                        <p className="text-xs text-gray-700">
                          <span className="font-semibold">Skills:</span>{' '}
                          {extractedJobData.skills.slice(0, 3).join(', ')}
                          {extractedJobData.skills.length > 3
                            ? ` +${extractedJobData.skills.length - 3} more`
                            : ''}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 text-center">
                      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-gray-100">
                        <DocumentArrowUpIcon className="h-5 w-5 text-gray-500" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        Drop your JD file here or <span className="text-[#007BFF] underline">browse</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        Supports PDF, DOC, DOCX (max {MAX_JD_FILE_SIZE_MB}MB)
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                  {extractedJobData ? (
                    <>
                      <button
                        type="button"
                        onClick={() => jdFileInputRef.current?.click()}
                        disabled={jdProcessing}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                      >
                        Replace file
                      </button>
                      <button
                        type="button"
                        onClick={handleJdRemoveFile}
                        disabled={jdProcessing}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                      >
                        Remove
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    data-testid="close-upload-modal-btn"
                    onClick={() => setShowJdUploadModal(false)}
                    disabled={jdProcessing}
                    className="px-3.5 py-1.5 text-xs font-semibold text-white bg-[#007BFF] rounded-lg hover:bg-[#0062CC] disabled:opacity-60"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className={`space-y-6 ${isModal ? 'space-y-4' : ''}`}
          >
            {/* Title and Category — compact row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              {/* Title */}
              <div className="flex flex-col">
                <label htmlFor="title" className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Assessment title <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Give your assessment a clear name to help you identify it later.
                </p>
                <input
                  type="text"
                  id="title"
                  name="title"
                  data-testid="assessment-title-input"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/25 text-sm"
                  placeholder="Enter Assessment Title"
                  maxLength={100}
                  required
                />
                <p className="mt-1 text-[10px] text-gray-400">{formData.title.length}/100 characters</p>
              </div>

              {/* Category dropdown */}
              <div className="relative flex flex-col">
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Category <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Choose the closest role family so we can tailor questions to that track.
                </p>
                <button
                  type="button"
                  data-testid="select-category-btn"
                  onClick={() => setIsCategoryOpen((open) => !open)}
                  className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-[#007BFF]/60 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30"
                >
                  <span className={formData.category ? '' : 'text-gray-400'}>
                    {formData.category
                      ? assessmentCategoryOptions.find((c) => c.value === formData.category)?.label ||
                      'Select category'
                      : 'Select category'}
                  </span>
                  <span
                    className={`ml-2 transform text-gray-400 transition-transform duration-150 ${isCategoryOpen ? 'rotate-180' : 'rotate-0'
                      }`}
                  >
                    ▾
                  </span>
                </button>
                {isCategoryOpen && (
                  <div className="absolute z-20 top-[calc(100%+0.25rem)] w-full origin-top rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 transition-all duration-150 ease-out">
                    <div className="max-h-64 overflow-y-auto py-1">
                      {assessmentCategoryOptions.map((option) => {
                        const selected = formData.category === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            data-testid={`category-option-${option.value}`}
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                category:
                                  prev.category === option.value ? '' : option.value,
                              }));
                              setIsCategoryOpen(false);
                            }}
                            className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${selected
                                ? 'bg-[#007BFF]/5 text-gray-900'
                                : 'hover:bg-gray-50 text-gray-800'
                              }`}
                          >
                            <span className="font-medium">{option.label}</span>
                            <span className="mt-0.5 text-xs text-gray-500">
                              {option.description}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* AI-only fields: JD Upload, Assessment Type, Question Types, Number of Questions */}
            {!isManualMode && (
              <>
                {/* Assessment Type — modern card-style chips */}
                {!isPassageReadingMode && (
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Assessment type <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Pick one or more to steer AI question generation (e.g. technical, behavioral).
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {assessmentTypeOptions.map((type) => {
                      const selected = selectedAssessmentTypes.includes(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          data-testid={`assessment-type-chip-${type.replace(/\s+/g, '-').toLowerCase()}`}
                          onClick={() => {
                            setSelectedAssessmentTypes((prev) =>
                              prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
                            );
                          }}
                          className={`
                            inline-flex items-center px-3 py-2 rounded-md text-sm font-medium
                            transition-all duration-200 select-none
                            ${selected
                              ? 'bg-[#007BFF] text-white border border-[#007BFF] shadow-sm shadow-[#007BFF]/15'
                              : 'bg-white text-gray-700 border border-gray-300 hover:border-[#007BFF]/60 hover:bg-[#007BFF]/5'
                            }
                          `}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Question Types — modern card-style chips */}
                {!isPassageReadingMode && (
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Question types <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Choose formats to include (e.g. multiple choice, short answer).
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {questionTypeUiOptions.map((opt) => {
                      const selected = opt.apiValues.every((v) => selectedQuestionTypes.includes(v));
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          data-testid={`question-type-chip-${opt.key}`}
                          onClick={() => {
                            setSelectedQuestionTypes((prev) => {
                              const allOn = opt.apiValues.every((v) => prev.includes(v));
                              if (allOn) {
                                return prev.filter((t) => !opt.apiValues.includes(t));
                              }
                              const next = [...prev];
                              for (const v of opt.apiValues) {
                                if (!next.includes(v)) next.push(v);
                              }
                              return next;
                            });
                          }}
                          className={`
                            inline-flex items-center px-3 py-2 rounded-md text-sm font-medium
                            transition-all duration-200 select-none
                            ${selected
                              ? 'bg-[#007BFF] text-white border border-[#007BFF] shadow-sm shadow-[#007BFF]/15'
                              : 'bg-white text-gray-700 border border-gray-300 hover:border-[#007BFF]/60 hover:bg-[#007BFF]/5'
                            }
                          `}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Number of Questions (duration is computed in Preview & Edit) */}
                <div>
                  <label
                    htmlFor="numberOfQuestions"
                    className="block text-sm font-semibold text-gray-800 mb-1.5"
                  >
                    {isPassageReadingMode ? 'Number of Passages (max 3)' : `Number of Questions (max ${MAX_AI_QUESTIONS})`} <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {isPassageReadingMode ? 'Target number of passages to generate.' : 'Target number of questions to generate.'}
                  </p>
                  <input
                    type="number"
                    id="numberOfQuestions"
                    name="numberOfQuestions"
                    value={formData.numberOfQuestions}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    min="1"
                    max={isPassageReadingMode ? 3 : MAX_AI_QUESTIONS}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/25 text-sm"
                    placeholder={isPassageReadingMode ? "1" : "10"}
                    required
                  />
                </div>

                {/* Additional Context */}
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="additionalContext"
                        className="block text-sm font-semibold text-gray-800 leading-tight mb-2"
                      >
                        Context <span className="text-red-500">*</span>
                      </label>
                      <p className="mt-0.5 mb-2 text-xs text-gray-500 leading-snug">
                        Tell the AI what to focus on so question generation stays relevant.
                      </p>
                    </div>
                    {shouldShowJdModalCta && (
                      <button
                        type="button"
                        data-testid="upload-jd-btn"
                        onClick={() => setShowJdUploadModal(true)}
                        disabled={jdProcessing}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#007BFF]/25 bg-[#007BFF]/5 px-3 py-1.5 text-xs font-semibold text-[#007BFF] hover:bg-[#007BFF]/10 disabled:cursor-not-allowed disabled:opacity-60 transition-all"
                      >
                        {jdProcessing ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                            Processing...
                          </span>
                        ) : (
                          <>
                            <DocumentTextIcon className="h-4 w-4" />
                            Upload JD
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <textarea
                    id="additionalContext"
                    name="additionalContext"
                    value={formData.additionalContext}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-all resize-none text-sm"
                    placeholder={isPassageReadingMode 
                      ? "Describe the type of passages you want (e.g., business communication, customer service scripts, technical explanations)..." 
                      : "Describe the role focus, key skills, must-evaluate topics, or any assessment priorities."}
                    required={!isManualMode}
                  />
                  {shouldShowJdModalCta && extractedJobData ? (
                    <div className="mt-2 flex items-center justify-end gap-2 text-xs">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                        <CheckIcon className="h-3.5 w-3.5" />
                        JD uploaded
                      </span>
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {/* Manual mode info */}
            {isManualMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Manual Mode:</strong> You'll add questions in the next step. Fill in the basic details above and click "Create Questions" to continue.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssessmentCreate;
